import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { AuthShell, FormInput, PrimaryButton, ErrorBanner } from "@/components/auth/AuthShell";

export default function RegisterPage() {
  const { register, loginWithGoogle, consumePendingPrompt } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      // If user arrived from homepage creator with a pending prompt, route to that page.
      const pending = consumePendingPrompt();
      if (pending?.kind === "video") {
        sessionStorage.setItem("ybm_autofill_prompt", JSON.stringify(pending));
        navigate("/generate/video");
      } else if (pending?.kind === "image") {
        sessionStorage.setItem("ybm_autofill_prompt", JSON.stringify(pending));
        navigate("/generate/image");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Get started"
      title="Create your Studios account"
      footer={
        <span>
          Already a member?{" "}
          <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="link-to-login">Sign in</Link>
        </span>
      }
    >
      <p className="text-[#A0A0A0] -mt-4 mb-8 text-sm leading-relaxed">
        Sign up and get <span className="text-[#D4AF37] font-semibold">25 starter credits</span> instantly — enough for a few free renders. Watermarked previews. No credit card required.
      </p>
      <form onSubmit={onSubmit} data-testid="register-form">
        <ErrorBanner message={error} testId="register-error" />
        <FormInput label="Name" value={name} onChange={setName} placeholder="Your name (optional)" autoComplete="name" testId="register-name-input" />
        <FormInput label="Email" type="email" value={email} onChange={setEmail} placeholder="you@business.com" autoComplete="email" required testId="register-email-input" />
        <FormInput label="Password" type="password" value={password} onChange={setPassword} placeholder="At least 8 characters" autoComplete="new-password" required testId="register-password-input" />
        <PrimaryButton disabled={loading} testId="register-submit-button">
          {loading ? "Creating account…" : "Create Account & Start Free Trial"}
        </PrimaryButton>
      </form>
      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-[#2A2A2A]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A]">or</span>
        <div className="flex-1 h-px bg-[#2A2A2A]" />
      </div>
      <button
        onClick={loginWithGoogle}
        type="button"
        data-testid="register-google-button"
        className="w-full inline-flex items-center justify-center gap-3 border border-[#2A2A2A] hover:border-[#F4F4F0] bg-[#0F0F0F] text-[#F4F4F0] py-3 transition-colors"
      >
        <GoogleIcon /> Continue with Google
      </button>
    </AuthShell>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.6-5.6C34 6.4 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5 44.5 36.3 44.5 25c0-1.5-.2-3-.5-4.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.6-5.6C34 6.4 29.3 4.5 24 4.5 16.2 4.5 9.4 8.9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.2-7.2 2.2-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.3 41.1 16.1 45.5 24 45.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.3 36.6 44.5 31.2 44.5 25c0-1.5-.2-3-.5-4.5z"/>
    </svg>
  );
}
